import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, FileText, Filter, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { talentTemplates, libraries, statuses } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Table, Pagination, Autocomplete } from '@anyemp/ui-kit';
import { PAGINATION_OPTIONS } from '../utils/paginationConstants';
import { loadColumnsStateFromLS, useTableColumns } from '../utils/tableColumnsPersistence';
import SearchInput from '../components/UI/SearchInput';
import StatusSelect from '../components/UI/StatusSelect';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import TalentTemplateModal from '../components/TalentTemplate/TalentTemplateModal';
import Avatar from '../components/UI/Avatar';
import ActivityFitPanel from '../components/UI/ActivityFitPanel';
import SavedFiltersDropdown from '../components/SavedFilters/SavedFiltersDropdown';
import { PAGE_KEYS } from '../constants/savedFilterPageKeys';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import DataViewToggle from '../components/UI/DataViewToggle';
import PageHeader from '../components/UI/PageHeader';
import ActionGroup from '../components/UI/ActionGroup';
import { CardSortControls, RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';

const SAVED_FILTER_KEYS = ['search', 'tool_id', 'inner_client_id', 'status_id', 'is_draft'];

const UserCell = ({ user, fallback }) => {
  if (!user) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">{fallback || '—'}</span>;
  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '—';
  return (
    <div className="flex items-center gap-1.5">
      <Avatar avatar={user.avatar} name={displayName} size="xs" />
      <span>{displayName}</span>
    </div>
  );
};

const BASE_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, alwaysPinned: true, alwaysVisible: true },
  { key: 'tool_id', label: 'Platform', sortable: false },
  { key: 'inner_client_id', label: 'Client', sortable: false },
  { key: 'status_id', label: 'Status', sortable: false },
  { key: 'prompt_id', label: 'Prompt', sortable: false },
  { key: 'sum_posts', label: 'Posts', sortable: true },
  { key: 'blocks_config', label: 'Blocks', sortable: false },
  { key: 'created_at', label: 'Created', sortable: true },
  { key: 'created_by', label: 'Created by', sortable: false },
  { key: 'updated_by', label: 'Updated by', sortable: false },
  { key: 'actions', label: 'Actions', sortable: false },
];

const TalentTemplates = () => {
  const { hasPermission } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });
  const [filters, setFilters] = useState({
    search: '',
    tool_id: '',
    inner_client_id: '',
    status_id: '',
    is_draft: '',
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'DESC'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isActivityFitOpen, setIsActivityFitOpen] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(null);

  const [toolOptions, setToolOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusRaw, setStatusRaw] = useState([]);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('talent-templates');
  const lookupsLoaded = useRef(false);

  const canCreate = hasPermission('talent-service', 'talent_template', 'create');
  const canUpdate = hasPermission('talent-service', 'talent_template', 'update');
  const canDelete = hasPermission('talent-service', 'talent_template', 'delete');

  useEffect(() => {
    if (!lookupsLoaded.current) loadLookups();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [filters]);

  const loadLookups = async () => {
    try {
      const [toolsRes, clientsRes, statusesRes] = await Promise.all([
        libraries.getContactTools().catch(() => ({ data: [] })),
        libraries.getInnerClients().catch(() => ({ data: [] })),
        statuses.getStatusesByType('talent_template', { isCommon: 1 }).catch(() => ({ data: [] }))
      ]);
      const tools = Array.isArray(toolsRes?.data) ? toolsRes.data : (Array.isArray(toolsRes) ? toolsRes : []);
      const clients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
      const statusList = Array.isArray(statusesRes?.data) ? statusesRes.data : (Array.isArray(statusesRes) ? statusesRes : []);
      setToolOptions(tools.map(t => ({ value: String(t.id), label: t.name || t.title })));
      setClientOptions(clients.map(c => ({ value: String(c.id), label: c.name })));
      setStatusRaw(statusList);
      setStatusOptions(statusList.map(s => ({ value: String(s.id), label: s.name })));
      lookupsLoaded.current = true;
    } catch (err) {
      console.error('Failed to load lookups:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      tool_id: '',
      inner_client_id: '',
      status_id: '',
      is_draft: '',
      page: 1,
      limit: 20,
      sort_by: 'created_at',
      sort_order: 'DESC'
    });
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      const res = await talentTemplates.getAll(params);
      const data = res?.data || [];
      const meta = res?.meta || {};
      setTemplates(Array.isArray(data) ? data : []);
      setPagination({
        current_page: meta.page || 1,
        total_pages: meta.totalPages || 1,
        total_items: meta.count || 0,
        items_per_page: filters.limit
      });
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await talentTemplates.remove(id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (templateId, statusId) => {
    setStatusChangeLoading(templateId);
    try {
      await talentTemplates.update(templateId, { status_id: statusId || null });
      const newStatus = statusRaw.find(s => String(s.id) === String(statusId)) || null;
      setTemplates(prev => prev.map(t =>
        t.id === templateId ? { ...t, status_id: statusId || null, status: newStatus } : t
      ));
      setEditingStatusId(null);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusChangeLoading(null);
    }
  };

  const rowKey = useCallback((template) => template.id, []);

  const handleSort = useCallback((key) => {
    setFilters(prev => ({
      ...prev,
      sort_by: key,
      sort_order: prev.sort_by === key && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
      page: 1
    }));
  }, []);

  const renderCell = (column, row) => {
    switch (column.key) {
      case 'name':
        return (
          <div className="flex items-center gap-2">
            <Link
              to={`/talent-templates/${row.id}`}
              className="font-medium text-light-text-primary dark:text-dark-text-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {row.name}
            </Link>
            {row.is_draft && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-700">
                Draft
              </span>
            )}
          </div>
        );
      case 'tool_id':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.tool?.name || row.tool?.title || (row.tool_id ? `#${row.tool_id}` : '—')}</span>;
      case 'inner_client_id':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.inner_client?.name || (row.inner_client_id ? `#${row.inner_client_id}` : '—')}</span>;
      case 'status_id':
        return (
          <StatusSelect
            currentStatus={row.status}
            statusOptions={statusRaw}
            isEditing={editingStatusId === row.id}
            isLoading={statusChangeLoading === row.id}
            onEdit={canUpdate ? () => setEditingStatusId(row.id) : undefined}
            onCancel={() => setEditingStatusId(null)}
            onChange={(statusId) => handleStatusChange(row.id, statusId)}
          />
        );
      case 'prompt_id':
        return row.prompt ? (
          <Link
            to={`/prompts/${row.prompt.id}`}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate max-w-[140px] block"
            title={row.prompt.name}
          >
            {row.prompt.name}
          </Link>
        ) : <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-xs">auto</span>;
      case 'sum_posts':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            {row.sum_posts || 0}
          </span>
        );
      case 'blocks_config': {
        const cfg = row.blocks_config || {};
        const active = Object.entries(cfg).filter(([, v]) => v).map(([k]) => k.slice(0, 3)).join(', ');
        return <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{active || '—'}</span>;
      }
      case 'created_at':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</span>;
      case 'created_by':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary"><UserCell user={row.createdByUser} fallback={row.created_by} /></span>;
      case 'updated_by':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary"><UserCell user={row.updatedByUser} fallback={row.updated_by} /></span>;
      default:
        return null;
    }
  };

  const renderActions = (row) => (
    <div className="flex items-center justify-end gap-2">
      {canUpdate && (
        <button
          onClick={() => { setEditingTemplate(row); setModalOpen(true); }}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-blue-600 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-blue-400"
          title="Edit"
        >
          <Edit size={16} />
        </button>
      )}
      {canDelete && (
        <button
          onClick={() => setDeleteTarget(row)}
          disabled={deletingId === row.id}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-red-600 disabled:opacity-50 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-red-400"
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );

  const columns = useMemo(
    () => BASE_COLUMNS.map((col) => ({
      ...col,
      render: col.key === 'actions'
        ? (row) => renderActions(row)
        : (row) => renderCell(col, row),
    })),
    [canUpdate, canDelete, deletingId]
  );

  const sorting = useMemo(
    () => ({ sort_by: filters.sort_by, sort_order: filters.sort_order }),
    [filters.sort_by, filters.sort_order]
  );

  const tableColumnsState = useTableColumns(columns, {
    ...loadColumnsStateFromLS('talentTemplates_columns', { defaultPinned: ['name'] }),
    onChange: (state) => {
      try { localStorage.setItem('talentTemplates_columns', JSON.stringify(state)); } catch {}
    },
  });

  const hasActiveFilters = filters.search || filters.tool_id || filters.inner_client_id || filters.status_id || filters.is_draft;

  const paginationControl = (
    <Pagination
      currentPage={pagination.current_page}
      totalPages={pagination.total_pages}
      totalItems={pagination.total_items}
      itemsPerPage={pagination.items_per_page}
      onPageChange={page => setFilters(prev => ({ ...prev, page }))}
      showResults="range"
      perPageOptions={PAGINATION_OPTIONS}
      perPage={filters.limit}
      onPerPageChange={limit => setFilters(prev => ({ ...prev, limit: parseInt(limit), page: 1 }))}
      showOnSinglePage
      hasFirstLast
      hasGoToPage
    />
  );

  return (
    <div className="min-h-full space-y-6 bg-light-bg-primary dark:bg-dark-bg-primary">
      {/* Header */}
      <PageHeader
        title={<span className="inline-flex flex-wrap items-center gap-3"><FileText className="text-blue-600" size={24} /> Talent Templates <span className="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">({pagination.total_items})</span></span>}
        actions={(
        <ActionGroup>
          <button
            onClick={() => setIsActivityFitOpen(true)}
            className="flex min-h-10 items-center gap-1.5 rounded-md border border-light-border-primary px-3 py-1.5 text-sm text-light-text-secondary transition-colors hover:bg-light-bg-secondary hover:text-light-text-primary dark:border-dark-border-primary dark:text-dark-text-secondary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
          >
            <Activity size={16} />
            Activity Feed
          </button>
          {canCreate && (
            <button
              onClick={() => { setEditingTemplate(null); setModalOpen(true); }}
              className="flex min-h-10 items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-700"
            >
              <Plus size={16} />
              Add Template
            </button>
          )}
          <DataViewToggle value={viewMode} onChange={setViewMode} />
        </ActionGroup>
        )}
      />

      {/* Filters panel */}
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">Filters</h3>
          <div className="flex flex-wrap items-center gap-3">
            <SavedFiltersDropdown
              pageKey={PAGE_KEYS.TALENT_TEMPLATES}
              currentFilters={filters}
              applyKeys={SAVED_FILTER_KEYS}
              onApply={(saved) => setFilters(prev => ({ ...prev, ...saved, page: 1 }))}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              <Filter size={14} />
              {showFilters ? 'Hide' : 'Show'} Filters
              {hasActiveFilters && !showFilters && (
                <span className="ml-1 w-2 h-2 rounded-full bg-primary-500 inline-block" />
              )}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Search</label>
                <SearchInput
                  value={filters.search}
                  onChange={val => handleFilterChange('search', val)}
                  placeholder="Search templates..."
                  debounceTime={300}
                  showIcon
                />
              </div>

              {/* Platform */}
              <Autocomplete
                clearable
                fullWidth
                label="Platform"
                options={toolOptions}
                value={filters.tool_id}
                onChange={val => handleFilterChange('tool_id', val || '')}
                placeholder="Any platform..."
              />

              {/* Inner Client */}
              <Autocomplete
                clearable
                fullWidth
                label="Inner Client"
                options={clientOptions}
                value={filters.inner_client_id}
                onChange={val => handleFilterChange('inner_client_id', val || '')}
                placeholder="Any client..."
              />

              {/* Status */}
              <Autocomplete
                clearable
                fullWidth
                label="Status"
                options={statusOptions}
                value={filters.status_id}
                onChange={val => handleFilterChange('status_id', val || '')}
                placeholder="Any status..."
              />

              {/* Draft */}
              <Autocomplete
                clearable
                fullWidth
                label="Draft"
                options={[
                  { value: 'false', label: 'Published' },
                  { value: 'true', label: 'Draft' }
                ]}
                value={filters.is_draft}
                onChange={val => handleFilterChange('is_draft', val || '')}
                placeholder="Any..."
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {viewMode === 'cards' && (
        <div className="space-y-4">
          <CardSortControls
            options={[{ value: 'name', label: 'Name' }, { value: 'sum_posts', label: 'Posts' }, { value: 'created_at', label: 'Created' }]}
            sortBy={filters.sort_by}
            sortOrder={filters.sort_order}
            onSortByChange={(sort_by) => setFilters(prev => ({ ...prev, sort_by, page: 1 }))}
            onSortOrderChange={(sort_order) => setFilters(prev => ({ ...prev, sort_order, page: 1 }))}
          />
          {loading ? <div className="py-12 text-center text-light-text-tertiary">Loading templates…</div> : templates.length ? (
            <ResponsiveCardGrid>
              {templates.map((row) => (
                <RecordCard key={row.id}>
                  <RecordCardHeader
                    title={<Link to={`/talent-templates/${row.id}`} className="hover:text-primary-600">{row.name}</Link>}
                    badge={row.is_draft ? <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Draft</span> : null}
                    actions={renderActions(row)}
                  />
                  <RecordCardFields>
                    <RecordCardField label="Platform">{row.tool?.name || row.tool?.title || '—'}</RecordCardField>
                    <RecordCardField label="Client">{row.inner_client?.name || '—'}</RecordCardField>
                    <RecordCardField label="Status">{renderCell({ key: 'status_id' }, row)}</RecordCardField>
                    <RecordCardField label="Posts">{row.sum_posts || 0}</RecordCardField>
                    <RecordCardField label="Created">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</RecordCardField>
                  </RecordCardFields>
                </RecordCard>
              ))}
            </ResponsiveCardGrid>
          ) : <div className="py-12 text-center text-light-text-tertiary">No templates found</div>}
          <div className="overflow-x-auto">{paginationControl}</div>
        </div>
      )}
      {viewMode === 'table' && <Table
        columns={columns}
        data={templates}
        isLoading={loading}
        rowKey={rowKey}
        sorting={sorting}
        onSort={handleSort}
        columnMenu
        {...tableColumnsState}
        emptyState={
          <div className="py-8 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
            No templates found
          </div>
        }
        footer={paginationControl}
      />}

      <TalentTemplateModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTemplate(null); }}
        template={editingTemplate}
        onSaved={fetchTemplates}
      />
      <ActivityFitPanel
        isOpen={isActivityFitOpen}
        onClose={() => setIsActivityFitOpen(false)}
        entityType="TalentTemplate"
        title="Activity Feed — Talent Templates"
      />
      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget.id)}
        title="Delete Talent Template"
        message={<>Delete template <strong>{deleteTarget?.name}</strong>? This action cannot be undone.</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deletingId === deleteTarget?.id}
      />
    </div>
  );
};

export default TalentTemplates;
